import React, { useState } from 'react';
import { Droplet, Thermometer, Users, Cable, Truck, Shield, Check, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { vanSpecs, whatsIncluded, notIncluded, vanImages } from '../data/mock';

const iconMap = {
  droplet: Droplet,
  thermometer: Thermometer,
  users: Users,
  cable: Cable,
  truck: Truck,
  shield: Shield,
};

const VehicleSection = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % vanImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + vanImages.length) % vanImages.length);
  };

  return (
    <section id="vehicle" className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block bg-sky-100 text-sky-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Vehicle Details
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Ford Transit Custom
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Professionally fitted for commercial window-cleaning use. Hot water system included.
          </p>
        </div>

        {/* Photo Gallery */}
        <div className="mb-16">
          <div className="relative max-w-4xl mx-auto">
            {/* Main Image */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-slate-100 max-h-[600px] flex items-center justify-center">
              <img
                src={vanImages[currentImageIndex].src}
                alt={vanImages[currentImageIndex].alt}
                className="w-full h-auto max-h-[600px] object-contain"
              />
              
              {/* Navigation Arrows */}
              <button
                onClick={prevImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-110"
              >
                <ChevronLeft className="w-6 h-6 text-slate-700" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-110"
              >
                <ChevronRight className="w-6 h-6 text-slate-700" />
              </button>

              {/* Image Counter */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-4 py-2 rounded-full text-sm font-medium">
                {currentImageIndex + 1} / {vanImages.length}
              </div>
            </div>

            {/* Thumbnail Strip */}
            <div className="flex justify-center gap-3 mt-4">
              {vanImages.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`w-20 h-14 rounded-lg overflow-hidden transition-all duration-200 ${
                    index === currentImageIndex 
                      ? 'ring-2 ring-sky-600 ring-offset-2 scale-105' 
                      : 'opacity-60 hover:opacity-100'
                  }`}
                >
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {vanSpecs.features.map((feature, index) => {
            const IconComponent = iconMap[feature.icon];
            return (
              <div
                key={index}
                className="group bg-slate-50 hover:bg-sky-50 rounded-2xl p-6 lg:p-8 transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
              >
                <div className="w-14 h-14 bg-sky-100 group-hover:bg-sky-200 rounded-xl flex items-center justify-center mb-5 transition-colors duration-300">
                  {IconComponent && <IconComponent className="w-7 h-7 text-sky-600" />}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            );
          })}
        </div>

        {/* What's Included / Not Included */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Included */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 lg:p-8">
            <h3 className="text-xl font-bold text-emerald-800 mb-6 flex items-center gap-2">
              <Check className="w-6 h-6" />
              What's Included
            </h3>
            <ul className="space-y-3">
              {whatsIncluded.map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-emerald-700">
                  <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Not Included */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 lg:p-8">
            <h3 className="text-xl font-bold text-amber-800 mb-6 flex items-center gap-2">
              <X className="w-6 h-6" />
              Not Supplied
            </h3>
            <ul className="space-y-3">
              {notIncluded.map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-amber-700">
                  <X className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VehicleSection;
